<template>
  <div v-if="show" class="modal-overlay">
    <div class="modal-content">
      <div class="modal-header">
        <h3>{{ isEditing ? 'Edit Category' : 'Add New Category' }}</h3>
        <button class="close-btn" @click="close">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <form @submit.prevent="handleSubmit">
          <div class="form-group">
            <label for="title">Title (English)</label>
            <input
              id="title"
              v-model="form.title"
              type="text"
              :class="{ 'error': errors.title }"
              placeholder="Enter category title"
            />
            <span v-if="errors.title" class="error-message">{{ errors.title }}</span>
          </div>

          <div class="form-group">
            <label for="title_ar">Title (Arabic)</label>
            <input
              id="title_ar"
              v-model="form.title_ar"
              type="text"
              :class="{ 'error': errors.title_ar }"
              placeholder="أدخل عنوان الفئة"
              dir="rtl"
            />
            <span v-if="errors.title_ar" class="error-message">{{ errors.title_ar }}</span>
          </div>

          <div class="form-group">
            <label>Image URL</label>
            <div class="file-uploader">
              <button @click="openUploadWidget('image_url')" class="upload-btn">
                <i class="fas fa-upload"></i> Upload Image
              </button>
              <div v-if="form.image_url" class="uploaded-file">
                <img :src="form.image_url" alt="Category Image" class="preview-image">
              </div>
            </div>
          </div>

          <div class="form-group">
            <label>Arabic Image URL</label>
            <div class="file-uploader">
              <button @click="openUploadWidget('image_url_ar')" class="upload-btn">
                <i class="fas fa-upload"></i> Upload Arabic Image
              </button>
              <div v-if="form.image_url_ar" class="uploaded-file">
                <img :src="form.image_url_ar" alt="Arabic Category Image" class="preview-image">
              </div>
            </div>
          </div>

          <div v-if="uploadError" class="error-message">{{ uploadError }}</div>

          <div class="form-group">
            <label for="description">Description (Optional)</label>
            <QuillEditor
              id="description"
              v-model:content="form.description"
              label="Description"
              :toolbar="toolbarOptions"
              placeholder="Enter category description"
            />
          </div>

          <div class="form-group">
            <label for="description_ar">Description (Arabic) (Optional)</label>
            <QuillEditor
              id="description_ar"
              v-model:content="form.description_ar"
              label="Description (Arabic)"
              :toolbar="toolbarOptions"
              placeholder="أدخل وصف الفئة"
            />
          </div>

          <div class="form-group">
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="form.is_active"
              />
              Active Category
            </label>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn-secondary" @click="close">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="loading">
              <span v-if="loading" class="loading-spinner"></span>
              {{ isEditing ? 'Update Category' : 'Create Category' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import cloudflareR2Service from '@/services/cloudflareR2'
import QuillEditor from '@/components/QuillEditor.vue';

export default {
  name: 'CategoryModal',
  components: {
    QuillEditor
  },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    category: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      form: {
        title: '',
        title_ar: '',
        image_url: '',
        image_url_ar: '',
        description: '',
        description_ar: '',
        is_active: true,
        order: 0
      },
      toolbarOptions: [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote'],
        [{ 'header': 1 }, { 'header': 2 }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        ['clean'],
        ['link']
      ],
      errors: {},
      loading: false,
      uploadError: ''
    }
  },
  computed: {
    isEditing() {
      return !!this.category
    }
  },
  watch: {
    show(newVal) {
      if (newVal && this.category) {
        this.form = { ...this.category }
      } else if (newVal) {
        this.resetForm()
      }
    }
  },
  methods: {
    resetForm() {
      this.form = {
        title: '',
        title_ar: '',
        image_url: '',
        image_url_ar: '',
        description: '',
        description_ar: '',
        is_active: true,
        order: 0
      }
      this.errors = {}
      this.uploadError = ''
    },
    validate() {
      this.errors = {}
      if (!this.form.title?.trim()) {
        this.errors.title = 'Title is required'
      } else if (this.form.title.length < 2) {
        this.errors.title = 'Title must be at least 2 characters'
      }
      
      if (!this.form.title_ar?.trim()) {
        this.errors.title_ar = 'Arabic title is required'
      } else if (this.form.title_ar.length < 2) {
        this.errors.title_ar = 'Arabic title must be at least 2 characters'
      }
      
      return Object.keys(this.errors).length === 0
    },
    async handleSubmit() {
      if (!this.validate()) return
      if (this.loading) return // Prevent double submission

      this.loading = true
      try {
        const payload = { ...this.form }
        await this.$emit('submit', payload)
        this.close()
      } catch (error) {
        console.error('Form submission error:', error)
      } finally {
        this.loading = false
      }
    },
    close() {
      this.resetForm()
      this.$emit('close')
    },
    // Cloudflare R2 upload handler
    async openUploadWidget(field) {
      this.uploadError = ""
      
      // Create a file input element programmatically
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*' // Only allow image files
      
      input.onchange = async (event) => {
        const file = event.target.files[0]
        if (!file) return
        
        try {
          const uploadResponse = await cloudflareR2Service.uploadWithValidation(file)
          // Update the field with the file URL
          this.form[field] = uploadResponse.fileUrl
        } catch (error) {
          this.uploadError = `Upload failed: ${error.message || "Unknown error"}`
        }
      }
      
      input.click()
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #111827;
}

.close-btn {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #111827;
}

.modal-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.form-group input.error,
.form-group textarea.error {
  border-color: #dc2626;
}

.error-message {
  color: #dc2626;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  display: block;
}

.modal-footer {
  margin-top: 2rem;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.btn-primary,
.btn-secondary {
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: #2563eb;
  color: white;
  border: none;
}

.btn-primary:hover {
  background: #1d4ed8;
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-secondary {
  background: white;
  color: #374151;
  border: 1px solid #e5e7eb;
}

.btn-secondary:hover {
  background: #f3f4f6;
}

.loading-spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid #fff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 0.5rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: auto;
  margin: 0;
  cursor: pointer;
}

input[type="number"] {
  -moz-appearance: textfield;
}

input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.file-uploader {
  margin-top: 8px;
}

.upload-btn {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  font: inherit;
  outline: inherit;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.uploaded-file {
  margin-top: 10px;
  width: 100%;
  border-radius: 6px;
  overflow: hidden;
  height: 150px;
}

.uploaded-file img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style> 